import type { ClaveKind } from '../../data/music-tracks';
import { SIM_HZ } from '../../data/tuning';

/**
 * Rhythm clock (GDD §14.3, §9.2, §15.3, issue #109): deterministic beat and clave pulses derived from the tick of the
 * simulation, so attacks that follow the music (JEFE-2, #110) are testable tick by tick. The music never drives the
 * core: the audio only hands over the tempo, the position and the output latency (src/audio/rhythm-source.ts); the
 * core only counts ticks. Pure; `src/core` imports nothing of `src/audio` (§15.2).
 *
 * The pulse n falls on the first tick t >= anchorTick + latencyTicks + n * 3600 / bpm, computed from the index and
 * never accumulated, so there is no drift at 97 BPM (beat 100 = ceil(100 x 3600 / 97) = 3712).
 */
/** Son clave 3-2: a two-bar cycle of 16 eighth notes (8 beats) with hits at these eighth steps (beats 0, 1.5, 3, 5, 6). */
export const SON_CLAVE_3_2_STEPS: readonly number[] = [0, 3, 6, 10, 12];
export const CLAVE_CYCLE_BEATS = 8;
export const CLAVE_CYCLE_EIGHTHS = 16;

export interface RhythmConfig {
  bpm: number;
  beatsPerBar: number;
  /** The tick at which beat 0 sounds (the music started). */
  anchorTick: number;
  clave: ClaveKind | null;
  /** The beat on which the first clave cycle (the bar "of 3") starts. */
  claveAnchorBeat: number;
  /** Output latency of the audio device in ticks: every pulse is delayed by it so it matches what is heard. */
  latencyTicks: number;
}

/** 60 s x 60 ticks. */
const TICKS_PER_MINUTE = 60 * SIM_HZ;
const EPSILON = 1e-9;

/** The tick of a position given in beats (fractional for the "and" of a beat). */
export function tickOfBeat(config: RhythmConfig, beat: number): number {
  return Math.ceil(config.anchorTick + config.latencyTicks + (beat * TICKS_PER_MINUTE) / config.bpm - EPSILON);
}

/** Position in beats of the clave hit `hit` (counted from 0 over the whole run). */
export function claveHitBeat(config: RhythmConfig, hit: number): number {
  const cycle = Math.floor(hit / SON_CLAVE_3_2_STEPS.length);
  const step = SON_CLAVE_3_2_STEPS[hit % SON_CLAVE_3_2_STEPS.length]!;
  return config.claveAnchorBeat + cycle * CLAVE_CYCLE_BEATS + step / 2;
}

export interface RhythmPulses {
  /** The tick the pulses belong to (0 on the first tick of the simulation). */
  tick: number;
  /** Index of the beat that sounds on this tick, if any. */
  beat: number | null;
  /** Index of the eighth note that sounds on this tick (twice per beat), if any. */
  eighth: number | null;
  /** Index of the clave hit (from 0 over the whole run) that sounds on this tick, if any, and its place in the cycle. */
  claveHit: { hit: number; cycle: number; step: number } | null;
}

/** What has already been emitted: each pulse once and in order, so nothing is lost nor repeated. */
export interface RhythmClockState {
  nextBeat: number;
  nextEighth: number;
  nextClaveHit: number;
  lastBeatTick: number | null;
  lastClaveHitTick: number | null;
}

export const createRhythmState = (): RhythmClockState => ({ nextBeat: 0, nextEighth: 0, nextClaveHit: 0, lastBeatTick: null, lastClaveHitTick: null });

/**
 * The pulses of one tick. At most one of each kind per tick: if a change of latency made two fall due together, the
 * second one comes on the next tick (never both lost, never one twice).
 */
export function stepRhythm(config: RhythmConfig, state: RhythmClockState, tick: number): RhythmPulses {
  const pulses: RhythmPulses = { tick, beat: null, eighth: null, claveHit: null };
  if (tickOfBeat(config, state.nextBeat) <= tick) {
    pulses.beat = state.nextBeat++;
    state.lastBeatTick = tick;
  }
  if (tickOfBeat(config, state.nextEighth / 2) <= tick) pulses.eighth = state.nextEighth++;
  if (config.clave && tickOfBeat(config, claveHitBeat(config, state.nextClaveHit)) <= tick) {
    const hit = state.nextClaveHit++;
    pulses.claveHit = { hit, cycle: Math.floor(hit / SON_CLAVE_3_2_STEPS.length), step: SON_CLAVE_3_2_STEPS[hit % SON_CLAVE_3_2_STEPS.length]! };
    state.lastClaveHitTick = tick;
  }
  return pulses;
}

/** Where the music is, for the test hooks: the beat, the eighth step inside the clave cycle and the last pulse ticks. */
export function rhythmView(config: RhythmConfig, state: RhythmClockState): {
  bpm: number;
  beat: number;
  cycleStep: number;
  lastBeatTick: number | null;
  lastClaveHitTick: number | null;
} {
  const beat = Math.max(0, state.nextBeat - 1);
  const eighths = Math.max(0, state.nextEighth - 1) - Math.round(config.claveAnchorBeat * 2);
  return {
    bpm: config.bpm,
    beat,
    cycleStep: ((eighths % CLAVE_CYCLE_EIGHTHS) + CLAVE_CYCLE_EIGHTHS) % CLAVE_CYCLE_EIGHTHS,
    lastBeatTick: state.lastBeatTick,
    lastClaveHitTick: state.lastClaveHitTick,
  };
}
